import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, X, Loader2 } from "lucide-react";
import { useCreateOwnSolution, useUpdateOwnSolution, useSyncSolutionProjects } from "@/hooks/useAIInventory";
import { ToolMultiSelect, ProjectMultiSelect, statusLabels } from "./shared";
import { toast } from "sonner";

interface SolutionsTabProps {
  solutions: any[];
  solutionProjectsMap: Record<string, string[]>;
  orgProjects: any[];
  businessAreas: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
  orgId: string | null;
  isSuperAdmin: boolean;
  selectedOrg: string;
  profileOrgId?: string;
  onDeleteConfirm: (item: { type: string; id: string; name: string }) => void;
}

interface SolutionFormProps {
  data: any;
  setData: (d: any) => void;
  businessAreas: any[];
  orgProjects: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
}

function SolutionForm({ data, setData, businessAreas, orgProjects, aiToolCatalog }: SolutionFormProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2"><Label>Nombre</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Ej: Asistente Legal IA" /></div>
      <div className="space-y-2"><Label>Descripción</Label><Textarea value={data.description || ''} onChange={(e) => setData({...data, description: e.target.value})} rows={2} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Área</Label>
          <Select value={data.area || undefined} onValueChange={(v) => setData({...data, area: v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar área" /></SelectTrigger>
            <SelectContent>{businessAreas.map((a: any) => <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estado</Label>
          <Select value={data.status} onValueChange={(v) => setData({...data, status: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2"><Label>Responsable</Label><Input value={data.owner || ''} onChange={(e) => setData({...data, owner: e.target.value})} /></div>
      <div className="space-y-2"><Label>Impacto de Negocio</Label><Input value={data.impact || ''} onChange={(e) => setData({...data, impact: e.target.value})} placeholder="Ej: Reducción 60% tiempo" /></div>
      <div className="space-y-2"><Label>Proyectos Vinculados</Label><ProjectMultiSelect selected={data.project_ids || []} onChange={(ids) => setData({...data, project_ids: ids})} projects={orgProjects} /></div>
      <div className="space-y-2"><Label>Herramientas Usadas</Label><ToolMultiSelect selected={data.tools_used || []} onChange={(t) => setData({...data, tools_used: t})} catalogTools={aiToolCatalog} /></div>
    </div>
  );
}

export default function SolutionsTab({ solutions, solutionProjectsMap, orgProjects, businessAreas, aiToolCatalog, orgId, isSuperAdmin, selectedOrg, profileOrgId, onDeleteConfirm }: SolutionsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [projectsModal, setProjectsModal] = useState<{ name: string; projectIds: string[] } | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");

  const [form, setForm] = useState({ name: "", description: "", status: "active", area: "", impact: "", tools_used: [] as string[], owner: "", project_ids: [] as string[] });

  const createSolution = useCreateOwnSolution();
  const updateSolution = useUpdateOwnSolution();
  const syncProjects = useSyncSolutionProjects();

  const areas = useMemo(() => [...new Set(solutions.map((s: any) => s.area).filter(Boolean))], [solutions]);

  const filtered = useMemo(() => {
    return solutions.filter((s: any) => {
      const matchesSearch = (s.name || '').toLowerCase().includes(search.toLowerCase()) || (s.description || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || s.status === statusFilter;
      const matchesArea = areaFilter === "all" || s.area === areaFilter;
      return matchesSearch && matchesStatus && matchesArea;
    });
  }, [solutions, search, statusFilter, areaFilter]);

  const getOrgIdForInsert = () => {
    if (isSuperAdmin) {
      if (selectedOrg === 'all') { toast.error('Selecciona una organización específica'); return null; }
      return selectedOrg;
    }
    return profileOrgId;
  };

  const handleAdd = () => {
    const oid = getOrgIdForInsert();
    if (!oid) return;
    createSolution.mutate({
      organization_id: oid, name: form.name, description: form.description, status: form.status,
      area: form.area, impact: form.impact, tools_used: form.tools_used, owner: form.owner || null,
    }, {
      onSuccess: (data: any) => {
        if (data?.id && form.project_ids.length > 0) syncProjects.mutate({ solutionId: data.id, projectIds: form.project_ids });
        setForm({ name: "", description: "", status: "active", area: "", impact: "", tools_used: [], owner: "", project_ids: [] });
        setIsDialogOpen(false);
      }
    });
  };

  const handleEdit = () => {
    if (!editing) return;
    updateSolution.mutate({
      id: editing.id, name: editing.name, description: editing.description, status: editing.status,
      area: editing.area, impact: editing.impact, tools_used: editing.tools_used || [], owner: editing.owner || null,
    }, {
      onSuccess: () => {
        syncProjects.mutate({ solutionId: editing.id, projectIds: editing.project_ids || [] });
        setEditing(null);
      }
    });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle>Soluciones de IA</CardTitle>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Agregar Solución</Button></DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Nueva Solución de IA</DialogTitle></DialogHeader>
                <SolutionForm data={form} setData={setForm} businessAreas={businessAreas} orgProjects={orgProjects} aiToolCatalog={aiToolCatalog} />
                <Button onClick={handleAdd} className="w-full mt-4" disabled={!form.name || createSolution.isPending}>
                  {createSolution.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Agregar Solución
                </Button>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar solución..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              {search && <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setSearch("")}><X className="h-3 w-3" /></Button>}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
            </Select>
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Área" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{areas.map((a: string) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            {(search || statusFilter !== "all" || areaFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatusFilter("all"); setAreaFilter("all"); }}>Limpiar filtros</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Solución</TableHead><TableHead>Responsable</TableHead><TableHead>Área</TableHead>
                <TableHead>Proyectos</TableHead><TableHead>Estado</TableHead><TableHead>Impacto</TableHead>
                <TableHead>Herramientas</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{solutions.length === 0 ? 'No hay soluciones registradas' : 'No se encontraron soluciones con los filtros aplicados'}</TableCell></TableRow>
              ) : filtered.map((sol: any) => {
                const projIds = solutionProjectsMap[sol.id] || [];
                return (
                  <TableRow key={sol.id}>
                    <TableCell><div><div className="font-medium">{sol.name}</div><div className="text-xs text-muted-foreground">{sol.description}</div></div></TableCell>
                    <TableCell className="text-sm">{sol.owner || '—'}</TableCell>
                    <TableCell>{sol.area || '—'}</TableCell>
                    <TableCell>
                      {projIds.length > 0 ? (
                        <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setProjectsModal({ name: sol.name, projectIds: projIds })}>
                          {projIds.length} proyecto{projIds.length !== 1 ? 's' : ''}
                        </Button>
                      ) : <span className="text-muted-foreground text-sm">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={sol.status === "active" ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}>
                        {sol.status === "active" ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{sol.impact || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(sol.tools_used || []).map((t: string, i: number) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
                        {(sol.tools_used || []).length === 0 && <span className="text-muted-foreground text-sm">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => setEditing({ ...sol, project_ids: solutionProjectsMap[sol.id] || [] })}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => onDeleteConfirm({ type: 'solution', id: sol.id, name: sol.name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Solution Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Solución</DialogTitle></DialogHeader>
          {editing && (
            <>
              <SolutionForm data={editing} setData={setEditing} businessAreas={businessAreas} orgProjects={orgProjects} aiToolCatalog={aiToolCatalog} />
              <Button onClick={handleEdit} className="w-full mt-4" disabled={updateSolution.isPending}>
                {updateSolution.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar Cambios
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Solution Projects Modal */}
      {projectsModal && (
        <Dialog open={!!projectsModal} onOpenChange={(o) => !o && setProjectsModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Proyectos de "{projectsModal.name}"</DialogTitle></DialogHeader>
            {(() => {
              const linked = projectsModal.projectIds.map(id => orgProjects.find(p => p.id === id)).filter(Boolean);
              return linked.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-6">No hay proyectos vinculados.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Proyecto</TableHead><TableHead>Estado</TableHead><TableHead>Participante</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {linked.map((p: any, i: number) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell><Badge variant="secondary">{statusLabels[p.status] || p.status}</Badge></TableCell>
                        <TableCell className="text-muted-foreground">{p.profiles?.full_name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
